import { useState } from 'react';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import { apiFetch } from '@diggens/api-client';
import type { RemovalCard as Removal } from '@diggens/types';

/**
 * A card asking the person to remove one of their own preferences, which the Chief of Staff proposed. Its text and state
 * come from the server, never from the reply, so it shows as plain text. Only a click on Remove removes anything: the API
 * decides the click for the signed-in person alone, and answers with the card in its new state.
 */
export function RemovalCard({ card, onDecided }: { card: Removal; onDecided: (card: Removal) => void }) {
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: 'remove' | 'keep') => {
    setDeciding(true);
    setError(null);
    try {
      const decided = await apiFetch<{ card: Removal }>(`/policies/removals/${encodeURIComponent(card.id)}/${decision}`, { method: 'POST' });
      onDecided(decided.card);
    } catch (failure) {
      console.error('Could not decide a removal card:', failure);
      setError('Your choice was not saved. Try again.');
    } finally {
      setDeciding(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
      <Typography variant="body2">{card.text}</Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {card.status === 'pending' && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button size="small" variant="contained" color="error" disabled={deciding} onClick={() => decide('remove')}>
            Remove
          </Button>
          <Button size="small" variant="outlined" disabled={deciding} onClick={() => decide('keep')}>
            Keep
          </Button>
        </Stack>
      )}
    </Paper>
  );
}
